import clone from 'clone';
import * as React from 'react';
import { ArticleList, ArticleType } from '../../../../amorg-common';
import { articlesClientApi } from '../../../../api/articlesClientApi';
import { Button } from '../../../../designSystem/button/Button';
import { AnimatedCollapsibleContent } from '../../../../designSystem/collapsible/AnimatedCollapsibleContent';
import { Flex, FlexItem } from '../../../../designSystem/compositeComponents/FlexHelpers';
import { FormField } from '../../../../designSystem/formField/FormField';
import { SelectField } from '../../../../designSystem/formField/SelectField';
import { TextInput } from '../../../../designSystem/inputs/TextInput';


export type ArticleFilter = {
    typeId?:string;
    listId?:string;
    topicId?:string;
    searchTerm?:string;
    released?:boolean;
}

type ArticleFilterPanelProps = {
    filter:ArticleFilter;
    buttons?:React.ReactNode[];
    onFilterChange:(newFilter:ArticleFilter) => void;
}

export function ArticleFilterPanel(props:ArticleFilterPanelProps):JSX.Element
{
    // const [showClassicFilter, setShowClassicFilter] = React.useState(true);
    const [lists, setLists] = React.useState<ArticleList[]>([]);
    const [types, setTypes] = React.useState<ArticleType[]>([]);
    const [searchTerm, setSearchTerm] = React.useState('');

    React.useEffect(() => {
        const getData = async () => {
            setLists(await articlesClientApi.getLists())
            setTypes(await articlesClientApi.getTypes());
        };
        getData();
        setSearchTerm(props.filter.searchTerm || '');
    },[]);

    async function handleFilterChange(newParams:ArticleFilter)
    {
        props.onFilterChange(newParams);
    }

    async function handleListFilterChange(listId:string)
    {
        const newFilter = clone(props.filter);
        if(listId === '')
        {
            newFilter.listId = undefined;
            newFilter.topicId = undefined;
        }
        else
        {
            newFilter.listId = listId;
            const list = lists.find(l => l.id === listId);
            if(!list?.topics.some(t => t.id === newFilter.topicId))
                newFilter.topicId = undefined;
        }

        handleFilterChange(newFilter);
    }

    async function handleTopicFilterChange(topicId:string)
    {
        const newFilter = clone(props.filter);
        if(topicId === '')
            newFilter.topicId = undefined;
        else
        {
            newFilter.topicId = topicId;
            newFilter.listId = lists.find(l => l.topics.some(t => t.id === topicId))?.id;
        }
        handleFilterChange(newFilter);
    }

    async function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>)
    {
        const value = e.currentTarget.value;
        
        if(e.key === 'Enter')
        {
            handleFilterChange({...props.filter, searchTerm:value});
        }
    }

    async function handleClearSearch()
    {
        setSearchTerm('');
        handleFilterChange({...props.filter, searchTerm:undefined});
    }

    return (
        <>
            <AnimatedCollapsibleContent open={true}>

                <Flex gap={32} justifyContent="stretch" alignItems='flex-end'>
                                
                    <FlexItem width="25%">
                        <SelectField
                            label='Liste'
                            selectedValue={props.filter.listId || ''}
                            emptyText="Alle lister"
                            options={lists.map(l => ({  text: l.name, value: l.id }))}
                            onChange={handleListFilterChange} 
                        />
                        </FlexItem>
                        
                        <FlexItem width="25%">
                        <SelectField
                            label='Emne'
                            selectedValue={props.filter.topicId || ''}
                            emptyText="Alle emner"
                            options={lists.map(l => 
                                ({text:l.name, options:l.topics.map(t => ({text:t.name, value:t.id }))}))} 
                            onChange={handleTopicFilterChange} 
                        />
                        </FlexItem>
                    
                        <FlexItem width="25%">
                        <SelectField
                            label='Type' 
                            selectedValue={props.filter.typeId || ''}
                            emptyText="Alle typer"
                            options={types.map(t => ({text:t.name, value:t.id }))}
                            onChange={(value) => handleFilterChange({...props.filter, typeId:value==='' ? undefined : value})} 
                        />
                        </FlexItem>
                        <FlexItem width="25%">
                        <SelectField
                            label='Status'
                            selectedValue={props.filter.released === true 
                                ? '1' 
                                : props.filter.released === false 
                                    ? '0' 
                                    : ''
                            }
                            emptyText="Alle"
                            options={[
                                {text:'Udgivet', value:'1'},
                                {text:'Ikke udgivet', value:'0'}
                            ]}
                            onChange={(value) => handleFilterChange({...props.filter,  
                                released:value==='1' 
                                    ? true 
                                    : value==='0' 
                                        ? false 
                                        : undefined})
                            }
                        />
                        </FlexItem>
                        <Flex gap={4} alignItems='center'>
                            <FormField
                                label=''
                                inputComponent={
                                    <Flex alignItems='center' gap={4}>
                                        <TextInput
                                            leadingIcon={'interface_actions_search'}
                                            placeholder='Søgetekst...'
                                            value={searchTerm}
                                            onChange={(value) => setSearchTerm(value)}
                                            onKeyDown={handleSearchKeyDown}
                                        />
                                        <Button variant="text" icon='cancel' onClick={handleClearSearch} />
                                    </Flex>
                                }
                            />
                        </Flex>
                </Flex>

            </AnimatedCollapsibleContent>
        </>
    );
}